import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, Users, UserCog, MessageSquare, 
  TrendingUp, Settings, Shield, Eye, CheckCircle, XCircle, 
  AlertTriangle, Clock, Activity, Lock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';

// Import sub-components
import PostManagement from '../components/admin/PostManagement';
import GhostAccounts from '../components/admin/GhostAccounts';
import UserManagement from '../components/admin/UserManagement';
import ContentModeration from '../components/admin/ContentModeration';
import NetworkGrowth from '../components/admin/NetworkGrowth';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    verifyAdminAccess();
  }, []);

  const verifyAdminAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      // Get current user info
      const userResponse = await axios.get(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const user = userResponse.data;

      // Check if user has admin role
      if (!user.role || user.role.toLowerCase() !== 'admin') {
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      // If admin, fetch dashboard stats
      fetchDashboardStats();
    } catch (error) {
      console.error('Error verifying admin access:', error);
      setAccessDenied(true);
      setLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-12 pb-12 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
              <Lock className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Access Denied</h2>
            <p className="text-gray-600 mb-6">
              You do not have administrator privileges to access this area.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Shield className="w-10 h-10" />
              <div>
                <h1 className="text-3xl font-bold">TrainerLinked.in Admin</h1>
                <p className="text-blue-100">Platform Management & Control</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-blue-100">Admin Dashboard</p>
                <p className="text-xs text-blue-200">Last updated: {new Date().toLocaleTimeString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-6 mb-8">
            <TabsTrigger value="overview" className="flex items-center space-x-2">
              <LayoutDashboard className="w-4 h-4" />
              <span>Overview</span>
            </TabsTrigger>
            <TabsTrigger value="growth" className="flex items-center space-x-2">
              <TrendingUp className="w-4 h-4" />
              <span>Growth</span>
            </TabsTrigger>
            <TabsTrigger value="posts" className="flex items-center space-x-2">
              <FileText className="w-4 h-4" />
              <span>Posts</span>
            </TabsTrigger>
            <TabsTrigger value="ghost" className="flex items-center space-x-2">
              <UserCog className="w-4 h-4" />
              <span>Ghosts</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span>Users</span>
            </TabsTrigger>
            <TabsTrigger value="moderation" className="flex items-center space-x-2">
              <MessageSquare className="w-4 h-4" />
              <span>Moderation</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Users Stats */}
                <Card className="hover:shadow-lg transition-shadow">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Users</CardTitle>
                    <Users className="w-5 h-5 text-blue-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-900">
                      {stats?.users?.total || 0}
                    </div>
                    <div className="flex items-center space-x-4 mt-2 text-sm">
                      <span className="text-green-600">↑ Trainers: {stats?.users?.trainers || 0}</span>
                      <span className="text-blue-600">Orgs: {stats?.users?.organizations || 0}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Content Stats */}
                <Card className="hover:shadow-lg transition-shadow">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Posts</CardTitle>
                    <FileText className="w-5 h-5 text-purple-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-900">
                      {stats?.content?.total_posts || 0}
                    </div>
                    <div className="mt-2 text-sm">
                      <span className="text-orange-600">⏳ Pending: {stats?.content?.pending_review || 0}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Opportunities */}
                <Card className="hover:shadow-lg transition-shadow">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Opportunities</CardTitle>
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-900">
                      {stats?.opportunities?.total || 0}
                    </div>
                    <div className="mt-2 text-sm">
                      <span className="text-green-600">✓ Active: {stats?.opportunities?.active || 0}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Ghost Accounts */}
                <Card className="hover:shadow-lg transition-shadow border-purple-200">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Ghost Accounts</CardTitle>
                    <UserCog className="w-5 h-5 text-purple-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-purple-600">
                      {stats?.users?.ghost_accounts || 0}
                    </div>
                    <div className="mt-2 text-sm text-purple-600">
                      Active Personas
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Activity className="w-5 h-5 mr-2" />
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={() => setActiveTab('posts')}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Review Posts
                    </Button>
                    <Button 
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      onClick={() => setActiveTab('ghost')}
                    >
                      <UserCog className="w-4 h-4 mr-2" />
                      Manage Ghosts
                    </Button>
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => setActiveTab('users')}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Verify Users
                    </Button>
                    <Button 
                      className="w-full bg-orange-600 hover:bg-orange-700"
                      onClick={() => setActiveTab('moderation')}
                    >
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Moderate Content
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Pending Reviews</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Clock className="w-5 h-5 text-orange-600" />
                          <div>
                            <p className="font-medium">Posts Awaiting Review</p>
                            <p className="text-sm text-gray-600">Requires immediate attention</p>
                          </div>
                        </div>
                        <span className="text-2xl font-bold text-orange-600">
                          {stats?.content?.pending_review || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Users className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="font-medium">Connection Requests</p>
                            <p className="text-sm text-gray-600">Pending approval</p>
                          </div>
                        </div>
                        <span className="text-2xl font-bold text-blue-600">
                          {stats?.connections?.pending_requests || 0}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Platform Health</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Content Approval Rate</span>
                          <span className="font-medium">85%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-green-600 h-2 rounded-full" style={{width: '85%'}}></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>User Verification Progress</span>
                          <span className="font-medium">62%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-blue-600 h-2 rounded-full" style={{width: '62%'}}></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Ghost Account Activity</span>
                          <span className="font-medium">78%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-purple-600 h-2 rounded-full" style={{width: '78%'}}></div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Growth Tab */}
          <TabsContent value="growth">
            <NetworkGrowth />
          </TabsContent>

          {/* Post Management Tab */}
          <TabsContent value="posts">
            <PostManagement />
          </TabsContent>

          {/* Ghost Accounts Tab */}
          <TabsContent value="ghost">
            <GhostAccounts />
          </TabsContent>

          {/* User Management Tab */}
          <TabsContent value="users">
            <UserManagement />
          </TabsContent>

          {/* Content Moderation Tab */}
          <TabsContent value="moderation">
            <ContentModeration />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
